// Ported from src/shared/api/cookies.js. Shared by both apps, because the cookie both of them lean
// on hardest is the same one: `active-client`, which the transport sends as X-Client-Id on every
// request. The dashboard keeps more here besides — the language, the theme-independent display
// preferences and the trading pairs on screen; the admin panel reads only the active client.
export function readCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

// A cookie name is not unique: the same name can exist once per (domain, path), and reading gives
// back only names and values — never the path a value came from. The browser lists them by
// descending path length, so readCookie's first match is whichever copy sits on the DEEPEST path,
// and a write at `path=/` cannot replace a deeper one; it just adds another.
//
// One stale copy is therefore permanent, and it silently shadows every write. That is what broke
// the client switcher: a leftover `active-client` at `path=/payment-links` (the default path of a
// cookie written from a /payment-links/… page, which is what react-cookie's option-less setCookie
// did) outranked the `path=/` one switchClient writes, so the reload read the OLD client back and
// the dashboard stayed where it was, with nothing on screen to explain why. Nothing about that is
// specific to one route or one app — any nested page either app writes from (/aml-screenings/:tab
// in the admin panel, say) can leave the same permanent shadow.
//
// Only a prefix of the current path can shadow this page, so clearing the name off every path
// segment of the current URL before writing is a complete fix rather than a guess.
const shadowingPaths = () => {
  const paths = [];
  let path = "";
  for (const segment of window.location.pathname.split("/").filter(Boolean)) {
    path += `/${segment}`;
    paths.push(path);
  }
  return paths;
};

const expireAt = (name, path) => {
  document.cookie = `${name}=; path=${path}; max-age=0; SameSite=Lax`;
};

// React called react-cookie's setCookie with no options, which writes a SESSION cookie — so that
// is the default here too. It matters for `active-client`: a persistent one would outlive the
// browser session and send the previous user's client id on a shared machine.
//
// Pass `days` only where outliving the session is the point (nothing does today; the argument
// exists so the choice has to be made deliberately rather than inherited).
export function writeCookie(name, value, days = null) {
  shadowingPaths().forEach((path) => expireAt(name, path));
  const lifetime = days === null ? "" : ` max-age=${days * 86400};`;
  document.cookie = `${name}=${value}; path=/;${lifetime} SameSite=Lax`;
}

// Logging out must drop the active client, or the next person to use this browser starts inside
// the previous user's account context until the clients list happens to overwrite it. A copy left
// on a deeper path would be exactly that, so this clears the same paths a write does.
export function deleteCookie(name) {
  shadowingPaths().forEach((path) => expireAt(name, path));
  expireAt(name, "/");
}
